import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiShoppingCart, FiMinus, FiPlus, FiMapPin, FiCheck } from 'react-icons/fi';
import api from '@/services/api';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, getTotalItems } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [added, setAdded] = useState(false);
  
  // Estados para variações
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [variationError, setVariationError] = useState('');

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
      
      const storeResponse = await api.get(`/stores/${response.data.store_id}`);
      setStore(storeResponse.data);
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Validar variações se necessário
    if (product.has_variations) {
      if (product.available_colors?.length > 0 && !selectedColor) {
        setVariationError('Selecione uma cor');
        return;
      }
      if (product.available_sizes?.length > 0 && !selectedSize) {
        setVariationError('Selecione um tamanho');
        return;
      }
    }
    setVariationError('');
    
    for (let i = 0; i < quantity; i++) {
      addToCart(store?.id || product.store_id, product, selectedColor, selectedSize);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleCheckout = () => {
    if (!user) {
      navigate(`/login?redirect=/produto/${id}`);
    } else {
      navigate('/cliente/carrinho');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Produto não encontrado</p>
      </div>
    );
  }

  const defaultImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTQwQzE3OS4wMDkgMTQwIDE2MiAxNTcuMDA5IDE2MiAxNzhDMTYyIDE5OC45OTEgMTc5LjAwOSAyMTYgMjAwIDIxNkMyMjAuOTkxIDIxNiAyMzggMTk4Ljk5MSAyMzggMTc4QzIzOCAxNTcuMDA5IDIyMC45OTEgMTQwIDIwMCAxNDBaIiBmaWxsPSIjOUI5QkEzIi8+CjxwYXRoIGQ9Ik0xMzAgMjYwQzEzMCAyMzkuMDEzIDE0Ny4wMTMgMjIyIDE2OCAyMjJIMjMyQzI1Mi45ODcgMjIyIDI3MCAyMzkuMDEzIDI3MCAyNjBWMjYwSDEzMFYyNjBaIiBmaWxsPSIjOUI5QkEzIi8+Cjwvc3ZnPg==';
  
  // Support both Cloudinary URLs, object format {url, public_id}, and legacy Base64 images
  const getImageSrc = (photo) => {
    if (!photo) return defaultImage;
    
    // Handle object format {url, public_id}
    if (typeof photo === 'object' && photo?.url) return photo.url;
    
    // Handle string formats
    if (typeof photo === 'string') {
      if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
      if (photo.startsWith('data:')) return photo;
      return `data:image/jpeg;base64,${photo}`;
    }
    
    return defaultImage;
  };
  
  const images = product.photos && product.photos.length > 0
    ? product.photos.map(p => getImageSrc(p))
    : [defaultImage];

  // Verificar se tem variações
  const hasColors = product.has_variations && product.available_colors?.length > 0;
  const hasSizes = product.has_variations && product.available_sizes?.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-40" data-testid="product-detail">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="font-semibold text-gray-800">Detalhes do Produto</h1>
          <button
            onClick={handleCheckout}
            className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiShoppingCart className="text-xl" />
            {getTotalItems() > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {getTotalItems()}
              </span>
            )}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto">
        {/* Image Gallery */}
        <div className="bg-white">
          <img
            src={images[selectedImage]}
            alt={product.name}
            className="w-full h-72 md:h-96 object-contain"
            onError={(e) => { e.target.src = defaultImage; }}
          />
          {images.length > 1 && (
            <div className="flex gap-2 p-4 overflow-x-auto">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${
                    selectedImage === index ? 'border-blue-500' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-4 space-y-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <span className={`inline-block px-2 py-1 text-xs rounded-full mb-2 ${
                  product.condition === 'novo' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {product.condition === 'novo' ? 'Novo' : 'Usado'}
                </span>
                <h1 className="text-xl font-bold text-gray-800">{product.name}</h1>
              </div>
              <span className="text-2xl font-bold text-[#DC143C]">
                {formatCurrency(product.price)}
              </span>
            </div>
            
            <p className="text-gray-600 mt-3">{product.description}</p>
            
            {product.stock > 0 ? (
              <p className="text-sm text-green-600 mt-2">
                {product.stock} unidades em estoque
              </p>
            ) : (
              <p className="text-sm text-red-600 mt-2">Produto esgotado</p>
            )}
          </div>

          {/* Seleção de Cor */}
          {hasColors && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3">🎨 Selecione a Cor</h3>
              <div className="flex flex-wrap gap-2">
                {product.available_colors.map(color => (
                  <button
                    key={color}
                    onClick={() => { setSelectedColor(color); setVariationError(''); }}
                    className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      selectedColor === color
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid={`color-${color}`}
                  >
                    {selectedColor === color && <FiCheck className="inline mr-1" size={14} />}
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Seleção de Tamanho */}
          {hasSizes && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3">📏 Selecione o Tamanho</h3>
              <div className="flex flex-wrap gap-2">
                {product.available_sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => { setSelectedSize(size); setVariationError(''); }}
                    className={`w-14 h-14 rounded-lg border-2 text-sm font-bold transition-all ${
                      selectedSize === size
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Erro de validação */}
          {variationError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
              ⚠️ {variationError}
            </div>
          )}

          {/* Store Info */}
          {store && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-2">Vendido por</h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  {store.logo ? (
                    <img src={store.logo} alt={store.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-xl font-bold text-gray-400">{store.name[0]}</span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-800">{store.name}</p>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <FiMapPin className="text-xs" />
                    <span>{store.address}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quantity Selector */}
          {product.stock > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3">Quantidade</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <FiMinus />
                </button>
                <span className="text-xl font-semibold w-8 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <FiPlus />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add to Cart Button */}
      {product.stock > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 space-y-2">
          {/* Resumo da seleção */}
          {(selectedColor || selectedSize) && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-2">
              {selectedColor && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                  🎨 {selectedColor}
                </span>
              )}
              {selectedSize && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                  📏 {selectedSize}
                </span>
              )}
            </div>
          )}
          
          <button
            onClick={handleAddToCart}
            disabled={added}
            className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${
              added 
                ? 'bg-green-500 text-white' 
                : 'bg-[#DC143C] text-white hover:bg-[#B01030]'
            }`}
            data-testid="add-to-cart-btn"
          >
            <FiShoppingCart />
            {added ? '✓ Adicionado!' : `Adicionar ao Carrinho - ${formatCurrency(product.price * quantity)}`}
          </button>
          
          {getTotalItems() > 0 && (
            <button
              onClick={handleCheckout}
              className="w-full py-3 border-2 border-[#DC143C] text-[#DC143C] rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors"
            >
              Ver Carrinho ({getTotalItems()} {getTotalItems() === 1 ? 'item' : 'itens'})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
